import { useEffect, useRef, useState } from "react";
import type { Call, Message } from "@aria/shared";
import { apiClient } from "@lib/apiClient";

/** Response of GET /history/search?q= — same row shapes as the list endpoints. */
export interface HistorySearchResults {
  calls: Call[];
  messages: Message[];
}

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

/**
 * Server-side augmentation for the Calls/Messages search boxes. While the
 * trimmed query is >= 2 characters, GET /history/search?q= is called after a
 * 300 ms debounce; a monotonically increasing request sequence discards stale
 * responses so a slow early request can never clobber a newer one. Any error
 * fails silently to `null` (callers fall back to client-only filtering).
 */
export function useHistorySearch(query: string): HistorySearchResults | null {
  const [results, setResults] = useState<HistorySearchResults | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      seqRef.current += 1; // invalidate anything still in flight
      setResults(null);
      return;
    }
    const seq = ++seqRef.current;
    const timer = window.setTimeout(() => {
      apiClient
        .get<HistorySearchResults>(`/history/search?q=${encodeURIComponent(q)}`)
        .then((res) => {
          if (seqRef.current === seq) setResults(res);
        })
        .catch(() => {
          if (seqRef.current === seq) setResults(null);
        });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  return results;
}
